import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Grid, Box, Paper, Pagination, Divider, Button } from '@mui/material';
import { Typography, Stack, CircularProgress } from '@mui/joy';
import CityCardItem from '../city/CityCardItem.jsx';
import Header from '../custom/Header.jsx';
import { getSortedToDestinations, calculateAvgValues, calculateMinCo2Mode } from '../../helpers/Functions.js';
// @ts-ignore
import destinations from '../../data/data5.json';
import Compare from './Compare.jsx';
import { useAuth } from 'context/AuthContext.jsx';
import { Colors } from 'helpers/Colors.js';
import BackgroundImage from '../../media/explore.jpg';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getCityInterests } from 'helpers/data.js';
import { useNavigationContext } from 'context/NavigationContext.jsx';
import Loader from 'components/custom/Loader.jsx';
import Snackbar from '@mui/joy/Snackbar';

const Explore = () => {
    const { fromDestination, interests, month } = useNavigationContext();
    const navigate = useNavigate();
    const [view, setView] = useState('card');
    const [sortBy, setSortBy] = useState('overall');
    const [page, setPage] = useState(1);
    const { profile, updateTravelHistory, removeTravelHistory, travelHistory } = useAuth();
    const [selectedSort, setSelectedSort] = useState('');
    const [loading, setLoading] = useState(false);
    const [historyInterests, setHistoryInterests] = useState([]);
    const [label, setLabel] = useState("Order by")
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.down('lg'));
    const itemsPerPage = selectedSort === '' ? (isMd ? 4 : 3) : (isMd ? 8 : 9);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = page * itemsPerPage;
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('Sign in to continue');
    useEffect(() => {
        // Simulate a network request or loading time
        setTimeout(() => setLoading(false), 3000);
    }, []);

    useEffect(() => {
        if (!fromDestination) {
            // Redirect to the homepage if fromDestination is empty or null
            navigate('/');
        }
    }, [fromDestination, navigate]);

    useEffect(() => {
        if (travelHistory && travelHistory.length > 0) {
            let commonInterestsSet = null; // Initialize as null to handle first intersection

            travelHistory?.forEach((history) => {
                const cityInterests = new Set(getCityInterests(history?.to)); // Convert interests to a Set for easy intersection

                if (commonInterestsSet === null) {
                    // Initialize with the first city's interests
                    commonInterestsSet = cityInterests;
                } else {
                    // Find intersection with the existing common interests
                    commonInterestsSet = new Set([...commonInterestsSet].filter(interest => cityInterests.has(interest)));
                }
            });
            setHistoryInterests(Array.from(commonInterestsSet || []))
        }
    }, [travelHistory, profile]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSortChange = (value) => {
        setSortBy(value);
        setPage(1);
        setSelectedSort(value);
        setTimeout(() => jumpToSection(value), 0);
        setLabel(value);
        console.log("sort", value);
    };

    const handleViewChange = (value) => {
        setView(value);
        setPage(1);
    };

    const isInHistory = (id) => {
        const bool = travelHistory.some(
            (entry) => entry.from === fromDestination.id && entry.to === id
        )

        return travelHistory.some(
            (entry) => entry.from === fromDestination.id && entry.to === id
        );
    };

    const handleItemClick = (id, val) => {
        handleOpen();   
        if (profile === null){
            setMessage("Sign in to continue");
            return isInHistory;
        }
        if (!val) {
            setMessage("Saved to Trips");
            handleSaveTravelHistory(id);

        } else {
            setMessage("Removed from Trips");
            handleRemoveTravelHistory(id);
        }


        return isInHistory;
    };

    const handleSaveTravelHistory = async (id) => {
        // Set loading state to true
        setLoading(true);

        await updateTravelHistory({
            from: fromDestination.id || "",
            to: id || "",
            month: month || "",
            mode: ""
        });

        // Set loading state to false
        setLoading(false);
    };

    const handleRemoveTravelHistory = async (id) => {
        setLoading(true);

        await removeTravelHistory({
            from: fromDestination.id || "",
            to: id || "",
            month: month || "",
            mode: ""
        });

        setLoading(false);
    };

    const getFilteredDestinations = () => {
        const sortedToDestinations = getSortedToDestinations(fromDestination, destinations, sortBy, month);
        return sortedToDestinations.filter((toDest) => interests.some((interest) => toDest?.interests.includes(interest)));
    };

    const getFilteredDestinations2 = (sortBy) => {
        const sortedToDestinations = getSortedToDestinations(fromDestination, destinations, sortBy, month);
        return sortedToDestinations.filter((toDest) => interests.some((interest) => toDest?.interests.includes(interest)));
    };

    const getFilteredDestinations3 = (sortBy, ints = interests) => {
        const sortedToDestinations = getSortedToDestinations(fromDestination, destinations, sortBy, month);
        const result = sortedToDestinations.filter((toDest) => ints.every((interest) => toDest?.interests.includes(interest)));
        if (result.length < 1) {
            return sortedToDestinations
                .filter((toDest) => ints.some((interest) => toDest?.interests.includes(interest)))
                .sort((a, b) => {
                    const aMatches = a.interests.filter((interest) => ints.includes(interest)).length;
                    const bMatches = b.interests.filter((interest) => ints.includes(interest)).length;
                    return bMatches - aMatches;
                });
        }
        return result;

    };

    const filteredDestinations = getFilteredDestinations();
    const overall = getFilteredDestinations2('overall');
    const emission = getFilteredDestinations2('emission');
    const popularity = getFilteredDestinations2('popularity');
    const seasonality = getFilteredDestinations2('seasonality');
    const interest = getFilteredDestinations3('overall');
    const history = getFilteredDestinations3('overall', historyInterests);
    const averages = calculateAvgValues(fromDestination, filteredDestinations, month);

    const jumpToSection = (sectionId) => {
        const targetElement = document.getElementById(sectionId);
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            console.log("Element with ID", sectionId, "not found");
        }
    }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const renderCityCardsWithLabel = (destinations, label, sort, id, sty = {}) => {
        return (
            <Box key={label} style={{ ...sty }} id={id}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        backgroundColor: Colors.white,
                    }}
                >
                    <Grid container
                        sx={{
                            minWidth: 0,
                            border: '2px solid',
                            borderShadow: 'md',
                            borderColor: Colors.gray,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            display: "flex",
                            p: 2
                        }}>
                        <Grid item xs={12} sm={10} md={10} lg={10} xl={9}>
                            <Typography gutterBottom level='h3'>
                                {label}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2} md={1.5} lg={1.5} xl={1.5}>
                            {selectedSort === '' ?
                                (<Button size="small" variant="contained" sx={{
                                    backgroundColor: Colors.green,
                                    '&:hover': {
                                        backgroundColor: Colors.darkGreen,
                                    },
                                }} onClick={() => {
                                    setSelectedSort(sort); setLabel(sort);
                                    handleSortChange(id);
                                }}>
                                    See More
                                </Button>)
                                :
                                (<Button size="small" variant="contained" sx={{
                                    backgroundColor: Colors.green,
                                    color: Colors.white,
                                    '&:hover': {
                                        backgroundColor: Colors.darkGreen,
                                    },
                                }} onClick={() => {
                                    setSelectedSort('');
                                    setPage(1);
                                    setLabel("Order by");
                                    setSortBy("overall");
                                }}>See Less
                                </Button>)
                            }
                        </Grid>
                    </Grid>
                </Box>
                <Grid container spacing={2} sx={{ p: 1 }}>
                    {destinations.slice(startIndex, endIndex).map((toDest, index) => {
                        const adjustedIndex = startIndex + index;
                        return (
                            <Grid item xs={12} sm={6} md={6} lg={4} xl={4} key={toDest.id}>
                                <CityCardItem
                                    index={adjustedIndex}
                                    fromDestination={fromDestination}
                                    toDestination={toDest}
                                    month={month}
                                    minCo2Mode={calculateMinCo2Mode(fromDestination, toDest)}
                                    averages={averages}
                                    sortBy={sort}
                                    clicked={isInHistory(toDest.id)}
                                    onItemClicked={() => {
                                        handleItemClick(toDest.id, isInHistory(toDest.id));
                                    }}
                                    interests={interests}
                                />
                            </Grid>
                        )
                    })
                    }
                </Grid>
            </Box >
        );
    };

    return (
        <Box sx={{ m: 3, mb: 5 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    height: "50vh",
                    overflow: "hidden",
                    margin: "10px auto",
                    backgroundImage: `url(${BackgroundImage})`, // Set the image as a background
                    backgroundSize: "cover", // Ensures the image covers the entire area
                    backgroundPosition: "center", // Centers the image content
                    backgroundRepeat: "no-repeat", // Prevents tiling
                }}
            >
                <Typography gutterBottom level='h3' sx={{
                    p: 4,
                    textAlign: 'center',
                    color: Colors.white,
                    mt: 10
                }}
                >
                    Discover Your Ideal Destinations: Top Travel Picks for <b>{month}</b> from <b>{fromDestination?.name}</b>!
                </Typography>
            </Box>
            <Divider />
            <Box >
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={open}
                    onClose={handleClose}
                    autoHideDuration={2000}
                    variant="soft"
                    color= {profile === null ? 'neutral' : 'success'}
                >
                    {message}
                </Snackbar>
            </Box>
            <Stack spacing={2} sx={{ pt: 1, minHeight: 0 }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        backgroundColor: Colors.white,
                    }}
                >
                    <Header sortBy={sortBy} handleSortChange={handleSortChange} handleViewChange={handleViewChange} isHistory={travelHistory.length > 0} label={label} />
                </Box>
                <Paper>
                    {view === 'card' ? (
                        <Box>
                            {
                                loading ? (
                                    <Loader />
                                ) : (
                                    <>
                                        {selectedSort === 'overall' && renderCityCardsWithLabel(overall, 'TravelGreen top recommendations', 'overall', 'overall', { "backgroundColor": "" })}
                                        {selectedSort === 'emission' && renderCityCardsWithLabel(emission, 'Recommendations based on Emission', 'emission', 'emission')}
                                        {selectedSort === 'popularity' && renderCityCardsWithLabel(popularity, 'Recommendations based on Popularity', 'popularity', 'popularity')}
                                        {selectedSort === 'seasonality' && renderCityCardsWithLabel(seasonality, 'Recommendations based on Seasonality', 'seasonality', 'seasonality')}
                                        {selectedSort === 'interest' && renderCityCardsWithLabel(interest, 'TravelGreen based on Interests', 'interest', 'interest')}
                                        {(travelHistory && travelHistory.length > 0) && selectedSort === 'history' && renderCityCardsWithLabel(history, 'Recommendations based on saved Trips', 'history', 'history')}

                                        {selectedSort === '' && (
                                            <>
                                                {renderCityCardsWithLabel(overall, 'TravelGreen top recommendations', 'overall', 'overall', { "backgroundColor": "" })}
                                                {renderCityCardsWithLabel(emission, 'Recommendations based on Emission', 'emission', 'emission')}
                                                {renderCityCardsWithLabel(popularity, 'Recommendations based on Popularity', 'popularity', 'popularity')}
                                                {renderCityCardsWithLabel(seasonality, 'Recommendations based on Seasonality', 'seasonality', 'seasonality')}
                                                {(interests?.length > 0) && renderCityCardsWithLabel(interest, 'Recommendations based on Interests', 'interest', 'interest')}
                                                {(travelHistory && travelHistory.length > 0) && renderCityCardsWithLabel(history, 'Recommendations based on saved Trips', 'history', 'history')}
                                            </>
                                        )}
                                    </>
                                )}
                        </Box>

                    ) : (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Compare filteredDestinations={filteredDestinations} from={fromDestination} />
                        </Grid>
                    )
                    }
                </Paper>
            </Stack>
            {
                view === 'card' && selectedSort && (
                    <>
                        <Pagination
                            sx={{
                                position: 'sticky',
                                zIndex: 1,
                                p: '9px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                color: Colors.green,
                                textColor: Colors.green
                            }}
                            count={
                                Math.ceil((selectedSort === 'interest' ?
                                    (interest.length) : selectedSort === 'history' ? history.length :
                                        (filteredDestinations.length))
                                    / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            shape='rounded'
                            color='standard'
                        />
                    </>

                )

            }
        </Box >
    );
};

export default Explore;
